import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Wordmark } from "@/components/Crest";
import { LanguageSelector } from "@/components/LanguageSelector";
import { Menu, X, ChevronDown } from "lucide-react";

const SCHOOLS = [
  { slug: "accelerated-studies", key: "nav_sch_acc" },
  { slug: "business-school", key: "nav_sch_biz" },
  { slug: "technology", key: "nav_sch_tech" },
  { slug: "education-institute", key: "nav_sch_edu" },
];

export function Nav({ transparent = false }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [schoolsOpen, setSchoolsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    if (!transparent) return;
    const on = () => setScrolled(window.scrollY > 70);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, [transparent]);

  const solid = !transparent || scrolled;
  const links = [
    { to: "/programmes", label: t("nav_programmes") },
    { to: "/admissions", label: t("nav_admissions") },
    { to: "/tuition", label: t("nav_tuition") },
    { to: "/faculty", label: t("nav_faculty") },
    { to: "/research", label: t("nav_research") },
    { to: "/about", label: t("nav_about") },
    { to: "/invest", label: t("nav_invest") },
  ];
  const link = solid ? "text-ink/75 hover:text-ink" : "text-white/85 hover:text-white";
  const headerCls = solid
    ? "fixed top-0 inset-x-0 z-50 border-b border-border bg-paper/90 backdrop-blur-md transition-colors duration-300"
    : "fixed top-0 inset-x-0 z-50 transition-colors duration-300";

  return (
    <header className={headerCls}>
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex h-16 items-center justify-between">
          <Link to="/" data-testid="nav-home-link" className="flex items-center gap-3">
            <Wordmark white={!solid} className="h-9 md:h-11" />
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            <div className="relative" onMouseEnter={() => setSchoolsOpen(true)} onMouseLeave={() => setSchoolsOpen(false)}>
              <button data-testid="nav-schools" className={`flex items-center gap-1 text-sm transition-colors ${link}`}>
                {t("nav_schools")} <ChevronDown className="h-3.5 w-3.5" />
              </button>
              {schoolsOpen && (
                <div className="absolute left-0 top-full pt-3 w-80">
                  <div className="border border-border bg-paper shadow-lg rounded-sm p-2">
                    {SCHOOLS.map((s) => (
                      <Link key={s.slug} to={`/schools/${s.slug}`} data-testid={`nav-school-${s.slug}`}
                        className="block px-3 py-2.5 text-sm text-ink/80 hover:bg-secondary hover:text-heraldic rounded-sm transition-colors">
                        {t(s.key)}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {links.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`nav-${l.to.slice(1)}`}
                className={`text-sm transition-colors ${loc.pathname === l.to ? "text-heraldic" : link}`}>
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-5">
            <LanguageSelector dark={!solid} />
            <Link to={user ? (["parent", "student"].includes(user.role) ? "/portal" : user.role === "applicant" ? "/centre" : "/admin") : "/login"}
              data-testid="nav-login" className={`text-sm ${link}`}>
              {user ? user.name.split(" ")[0] : t("nav_login")}
            </Link>
            <Link to="/apply" data-testid="nav-apply-cta"
              className="bg-heraldic text-white text-sm font-medium px-5 py-2 rounded-sm hover:bg-heraldic-700 transition-colors">
              {t("nav_apply")}
            </Link>
          </div>

          <button className={`lg:hidden ${solid ? "text-ink" : "text-white"}`} data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)}>
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border bg-paper px-5 py-4 space-y-1">
          {SCHOOLS.map((s) => (
            <Link key={s.slug} to={`/schools/${s.slug}`} onClick={() => setOpen(false)} className="block py-2 text-sm text-ink/80">{t(s.key)}</Link>
          ))}
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block py-2 text-sm text-ink/80">{l.label}</Link>
          ))}
          <div className="flex items-center justify-between pt-3 border-t border-border mt-3">
            <LanguageSelector />
            <Link to="/apply" onClick={() => setOpen(false)} className="bg-heraldic text-white text-sm px-5 py-2 rounded-sm">{t("nav_apply")}</Link>
          </div>
        </div>
      )}
    </header>
  );
}
